import { BatchRawFile, PagedBatchRawFileList } from "@domain/schemas/BI";
import { DataResult, process } from "@progress/kendo-data-query";
import { GridDetailRowProps, GRID_PREVENT_SELECTION_ELEMENT, GridDataStateChangeEvent, getSelectedState, GridSelectionChangeEvent, GridColumn, Grid } from "@progress/kendo-react-grid";
import React, { useEffect, useState } from "react";
import { DATA_ITEM_KEY, SELECTED_FIELD } from "@presentation/components/helpers/constants";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ListJobsService } from "@application/useCases";
import { createDetailDataGridStore, DetailDataGridStore, SampleListItemState } from "@presentation/common";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

const useStepsGridStore = createDetailDataGridStore<SampleListItemState, BatchRawFile>();

const copyTextToClipboard = async (text: string) => {
  if (!text) {
    return;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const fallbackTextArea = document.createElement("textarea");
  fallbackTextArea.value = text;
  fallbackTextArea.setAttribute("readonly", "");
  fallbackTextArea.style.position = "absolute";
  fallbackTextArea.style.left = "-9999px";
  document.body.append(fallbackTextArea);
  fallbackTextArea.select();
  document.execCommand("copy");
  fallbackTextArea.remove();
};

export const SamplesView = (properties: GridDetailRowProps) => {
  const batchId: string = properties.dataItem.id;
  const listJobsService = useInjection(ListJobsService);

  const { detailList, metaState, selectedState, setDetailList, setMetaStateForDetail, setSelectedStateForDetail } = useStepsGridStore(
    useShallow((state: DetailDataGridStore<SampleListItemState, BatchRawFile>) => ({
      detailList: state.detailDictionary[batchId]?.detailList,
      metaState: state.detailDictionary[batchId]?.metaState,
      selectedState: state.detailDictionary[batchId]?.selectedState,
      setDetailList: state.setDetailList,
      setMetaStateForDetail: state.setMetaStateForDetail,
      setSelectedStateForDetail: state.setSelectedStateForDetail,
    })),
  );

  const [dataResult, setDataResult] = useState<DataResult>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [copiedFileName, setCopiedFileName] = useState<string | null>(null);
  const { t: loc } = useTranslation();

  const handleCopyFileName = async (fileName: string) => {
    try {
      await copyTextToClipboard(fileName);
      setCopiedFileName(fileName);
      setTimeout(() => {
        setCopiedFileName((current) => (current === fileName ? null : current));
      }, 1200);
    } catch (error) {
      console.error(loc("errorMessage", { error }));
    }
  };

  const load = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
    } catch (error) {
      // Handle any errors here
      console.error(loc("errorMessage", { error: error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSampleList = async () => {
      const batchSampleListResponse: PagedBatchRawFileList = await listJobsService.getJobBatchSampleListAsync("none", "none", batchId);
      const batchSampleList = batchSampleListResponse.list!.map((sample) => ({
        ...sample,
        fileSize: sample.sizeInBytes ? +(sample.sizeInBytes / 1_000_000_000).toFixed(2) : null,
      }));
      setDetailList(batchId, batchSampleList);
    };
    fetchSampleList();
  }, []);

  useEffect(() => {
    load(async () => {
      if (detailList && metaState) setDataResult(process(detailList, metaState));
    });
  }, [metaState, detailList]);

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setSelectedStateForDetail(batchId, newSelectedState);
  };

  const handleDataStateChange = (e: GridDataStateChangeEvent) => {
    setMetaStateForDetail(batchId, e.dataState);
    setDataResult(process(detailList, e.dataState));
  };

  if (detailList && !isLoading) {
    return (
      <div {...{ [GRID_PREVENT_SELECTION_ELEMENT]: true }} style={{ width: "100%" }}>
        <Grid
          style={{ maxHeight: "40vh", width: "100%" }}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          scrollable="scrollable"
          resizable={true}
          sortable={true}
          filterable={true}
          navigatable={false}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
          {...metaState}
          data={dataResult}
          onDataStateChange={handleDataStateChange}
          onSelectionChange={handleSelectionChange}
        >
          <GridColumn
            field="fileName"
            title="Raw File Name"
            width="550"
            filter="text"
            cell={(cellProperties) => {
              const fileName = String(cellProperties.dataItem?.fileName ?? "");
              const isCopied = copiedFileName === fileName;

              return (
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", minWidth: 0 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", userSelect: "text", minWidth: 0 }} title={fileName}>
                      {fileName}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        void handleCopyFileName(fileName);
                      }}
                      style={{
                        border: "1px solid #cbd5e1",
                        borderRadius: "4px",
                        backgroundColor: isCopied ? "#dcfce7" : "#ffffff",
                        color: isCopied ? "#166534" : "#1f2937",
                        padding: "2px 8px",
                        fontSize: "12px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                      aria-label={`Copy raw file name ${fileName}`}
                      title={isCopied ? "Copied" : "Copy file name"}
                    >
                      {isCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </td>
              );
            }}
          />
          <GridColumn field="location" title="Storage Location" width="350" filter="text" />
          <GridColumn field="fileSize" title={loc("fileSizeInGiB")} width="220px" minResizableWidth={220} filter="numeric" />
          <GridColumn width="auto" minResizableWidth={0} filterable={false} />
        </Grid>
      </div>
    );
  }
  return (
    <div style={{ height: "50px", width: "100%" }}>
      <div style={{ position: "absolute", width: "100%" }}>
        <div className="k-loading-image" />
      </div>
    </div>
  );
};
